import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";
import { ArrowLeft, Crown, Trophy, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";

const RANK_BADGE = {
    "Champion": { color: "text-wta-accent", icon: Crown, weight: "fill" },
    "Runner-up": { color: "text-white", icon: Trophy, weight: "fill" },
    "3rd Place": { color: "text-wta-muted", icon: Trophy, weight: "duotone" },
    "Semifinalist": { color: "text-wta-muted", icon: Trophy, weight: "regular" },
    "Quarterfinalist": { color: "text-wta-muted", icon: Trophy, weight: "regular" },
};

export default function CompetitionRecap() {
    const { id } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.get(`/competitions/${id}/recap`)
            .then(({ data }) => setData(data))
            .catch((e) => setErr(formatErr(e.response?.data?.detail) || "Could not load recap"));
    }, [id]);

    const share = async () => {
        const url = window.location.href;
        try {
            if (navigator.share) {
                await navigator.share({ title: `${data?.competition?.title} — Recap`, url });
            } else {
                await navigator.clipboard.writeText(url);
                toast.success("Link copied");
            }
        } catch { /* user cancelled */ }
    };

    if (err) {
        return (
            <div className="min-h-[100dvh] flex flex-col items-center justify-center p-8 text-center">
                <div className="font-heading text-4xl tracking-tight">Recap unavailable</div>
                <div className="text-wta-muted mt-2">{err}</div>
                <button onClick={() => nav(-1)} className="btn-ghost mt-6 px-5 py-2 rounded-sm text-sm font-heading uppercase tracking-wider">Back</button>
            </div>
        );
    }
    if (!data) return <div className="p-10 text-wta-muted">Loading recap…</div>;

    if (!data.clips?.length) {
        return (
            <div className="min-h-[100dvh] flex flex-col items-center justify-center p-8 text-center">
                <div className="font-heading text-4xl tracking-tight">No clips available</div>
                <div className="text-wta-muted mt-2">Winners didn't submit videos.</div>
                <Link to={`/competitions/${id}`} className="btn-ghost mt-6 px-5 py-2 rounded-sm text-sm font-heading uppercase tracking-wider">Back to competition</Link>
            </div>
        );
    }

    return (
        <div className="bg-black min-h-[100dvh]">
            {/* Header overlay */}
            <div className="fixed top-0 inset-x-0 z-30 flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent">
                <button data-testid="recap-back" onClick={() => nav(-1)} className="text-white flex items-center gap-1 text-sm">
                    <ArrowLeft size={16} /> Back
                </button>
                <div className="font-heading text-sm tracking-[0.4em] text-wta-muted uppercase truncate max-w-[60vw]">
                    {data.competition.title} · Recap
                </div>
                <button data-testid="recap-share" onClick={share} className="text-white flex items-center gap-1 text-sm">
                    <ShareNetwork size={16} /> Share
                </button>
            </div>

            {/* Snap-scroll vertical reel */}
            <div className="snap-feed">
                {data.clips.map((clip, idx) => (
                    <RecapClip key={clip.entry_id} clip={clip} idx={idx} total={data.clips.length} />
                ))}
            </div>
        </div>
    );
}

function RecapClip({ clip, idx, total }) {
    const ref = useRef(null);
    const [playing, setPlaying] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    el.play().then(() => setPlaying(true)).catch(() => {});
                } else {
                    el.pause();
                    setPlaying(false);
                }
            },
            { threshold: 0.7 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    const badge = RANK_BADGE[clip.rank] || RANK_BADGE["Quarterfinalist"];
    const Icon = badge.icon;

    return (
        <div className="snap-item relative bg-black flex items-center justify-center">
            <video
                ref={ref}
                src={clip.video_url}
                poster={clip.thumbnail || undefined}
                playsInline
                loop
                muted={false}
                controls={false}
                className="max-h-full max-w-full object-contain"
                onClick={() => {
                    const v = ref.current;
                    if (!v) return;
                    if (playing) v.pause();
                    else v.play().catch(() => { /* ignore source/autoplay rejection */ });
                }}
                data-testid={`recap-clip-${idx}`}
            />
            {/* Rank overlay */}
            <div className="absolute top-16 left-4 right-4 flex items-start gap-3 pointer-events-none">
                <div className={`${badge.color} shrink-0`}>
                    <Icon size={28} weight={badge.weight} />
                </div>
                <div>
                    <div className={`font-heading text-xs tracking-[0.4em] uppercase ${badge.color}`}>{clip.rank}</div>
                    <div className="font-heading text-2xl md:text-4xl tracking-tight text-white mt-1 leading-none">
                        @{clip.author?.name || "—"}
                    </div>
                    {clip.title && <div className="text-white/80 text-sm mt-1 max-w-xs">{clip.title}</div>}
                    {clip.prize > 0 && (
                        <div className="font-heading text-lg text-wta-accent mt-2">${clip.prize.toFixed(0)} prize</div>
                    )}
                </div>
            </div>
            {/* Pagination dots */}
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5 pointer-events-none">
                {Array.from({ length: total }).map((_, i) => (
                    <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === idx ? "bg-wta-primary" : "bg-white/30"}`} />
                ))}
            </div>
        </div>
    );
}
